import { cn } from "@/lib/utils";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { formatDateTime, isNullCheck } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { ColumnType } from "@utils/interface/index.interface";
import { EnrollmentData } from "@utils/interface/journey.interface";
import Tooltip from "@ui/external/components/tooltip";
import { useMemo } from "react";
import { useParams } from "react-router";

const ColumnList = () => {
  const { handleAction } = useHandleAction({});
  const params = useParams();
  console.log("params", params);

  const column: ColumnType<EnrollmentData>[] = useMemo(
    () => [
      {
        name: "ID",
        cell: (row) => (
          <span className="font-semibold text-gray-700">{row?.id}</span>
        ),
        sortable: true,
      },

      {
        name: "State",
        cell: (row) => (
          <span className="font-semibold text-gray-700">
            <Badge variant="secondary" className="text-yellow-500">
              {row?.is_student ? "Student" : "Non-Student"}
            </Badge>
          </span>
        ),
        sortable: true,
      },
      {
        name: "Full Name",
        accessKey: "full_name",
        cell: (row) => (
          <Tooltip description={row?.name || row?.full_name || "Null"}>
            <div className="text-sm font-medium text-gray-800">
              {isNullCheck(row?.name || row?.full_name) as string}
            </div>
          </Tooltip>
        ),
        sortable: false,
      },
      {
        name: "Email",
        accessKey: "email",
        cell: (row) => (
          <Tooltip
            description={
              row?.email || row?.email_address || row?.recipient || "Null"
            }
          >
            <div className="text-sm font-medium text-gray-800 max-w-[140px] truncate w-fit">
              <a
                href={`mailto:${
                  row?.email || row?.email_address || row?.recipient
                }`}
              >
                {" "}
                {
                  isNullCheck(
                    row?.email || row?.email_address || row?.recipient,
                  ) as string
                }
              </a>
            </div>
          </Tooltip>
        ),
        sortable: false,
      },
      {
        name: "Phone Number",
        accessKey: "phone_number",
        cell: (row) => (
          <div className="text-sm text-gray-600">
            <a href={`tel:${row?.phone_number})`}>
              {" "}
              {isNullCheck(row?.phone_number) as string}
            </a>
          </div>
        ),
        sortable: true,
      },
      {
        name: "Address",
        accessKey: "address",
        cell: (row) => (
          <Tooltip description={row?.address || "Null"}>
            <div className="text-sm text-gray-700 max-w-[140px] w-fit truncate">
              {isNullCheck(row?.address) as string}
            </div>
          </Tooltip>
        ),
        sortable: true,
      },
      {
        name: "Course",
        accessKey: "interested_courses__title",
        cell: (row) => {
          const coursesName = Array.isArray(row?.course)
            ? row?.course.length > 0
              ? row?.course?.map((item) => item?.title).join(", ")
              : "N/a"
            : // @ts-ignore
              (isNullCheck(row?.course?.title) as string);

          return (
            <Tooltip description={coursesName || "Null"}>
              <div className="text-sm max-w-[150px] w-fit truncate text-gray-700">
                {coursesName}
              </div>
            </Tooltip>
          );
        },
        sortable: true,
      },
      {
        name: "Education Level",
        cell: (row) => (
          <Tooltip description={row?.education_level}>
            <div className="text-sm text-gray-700 max-w-[140px]">
              {isNullCheck(row?.education_level) as string}
            </div>
          </Tooltip>
        ),
        sortable: true,
      },
      {
        name: "Learning Format",
        cell: (row) => (
          <div className="text-sm text-gray-700">
            {isNullCheck(row?.learning_format) as string}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Laptop",
        cell: (row) => (
          <div className="text-sm text-gray-700">
            {(isNullCheck(row?.has_laptop) as boolean) === true ? "Yes" : "No"}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Payment Amount",
        cell: (row) => (
          <div className="text-sm text-gray-700">
            {isNullCheck(row?.course?.amount) as string}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Subject",
        cell: (row) => (
          <div className="text-sm flex gap-2 items-center capitalize text-gray-700">
            <span
              className={cn(
                "w-[10px] block h-[10px] rounded-full ",
                {
                  others: "bg-[#f59e0b]",
                  certificate: "bg-[#1DA077]",
                  course: "bg-blue-500",
                }[row?.subjects[0]?.name?.toLowerCase() as string] ||
                  "bg-[#C33241]",
              )}
            ></span>

            {row?.subjects?.length > 0
              ? row?.subjects?.map((item) => item?.name).join(",")
              : "N/a"}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Created Date",
        accessKey: "created_date",
        cell: (row) => {
          return (
            <span className="font-semibold text-gray-700">
              {formatDateTime(row?.created_date)}
            </span>
          );
        },
        sortable: true,
      },
      {
        name: "Source",
        cell: (row) => (
          <Tooltip description={row?.source?.name || "Null"}>
            <div className="  max-w-[140px] truncate">
              {row?.source?.name ? (
                <Badge
                  className={cn(
                    "text-yellow-500 bg-transparent border-1 border-yellow-500",
                    "capitalize",
                  )}
                >
                  {row?.source?.name || "Null"}
                </Badge>
              ) : (
                ""
              )}
            </div>
          </Tooltip>
        ),
      },
      {
        name: "Referred By",
        cell: (row) => (
          <Tooltip description={row?.referred_by || "Null"}>
            <div className="max-w-[140px] truncate">
              <span className="font-semibold text-gray-700 ">
                {row?.referred_by || " "}
              </span>
            </div>
          </Tooltip>
        ),
      },
      {
        name: "Unique code",
        accessKey: "unique_code",
        cell: (row) => {
          return (
            <span className="font-semibold text-gray-700">{row?.code}</span>
          );
        },
        sortable: true,
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="flex items-center gap-3 text-[#1DA077] cursor-pointer">
            {params?.type == "request-form" ? null : (
              <span
                className="cursor-pointer"
                onClick={() => {
                  handleAction({ type: "close" });
                  params.type == "contact-form" ||
                  params.type == "enrollment" ||
                  params.type == "masterclass-enrollment"
                    ? handleAction({ type: "drawer-view", data: row?.id })
                    : handleAction({ type: "modal-view", data: row?.id });
                }}
              >
                View
              </span>
            )}
            {/* {params.type == "contact-form" ||
            params.type == "request-form" ? null : (
              <span
                onClick={() =>
                  handleAction({ type: "modal-edit", data: row?.id })
                }
              >
                Edit
              </span>
            )} */}
            <span
              className="text-[#C33241] cursor-pointer"
              onClick={() => {
                handleAction({
                  type: "confirm",
                  data: { type: "delete", id: row?.id },
                });
              }}
            >
              Delete
            </span>
          </div>
        ),
        sortable: false,
      },
      // {
      //   name: "Payment Status",
      //   cell: (row) => (
      //     <div className="text-sm text-gray-700">
      //       {isNullCheck(row?.course?.amount) as string}
      //     </div>
      //   ),
      //   sortable: true,
      // },
      {
        name: "Convert To Student",
        cell: (row) => (
          <div className="text-sm text-gray-700">
            {row?.is_student ? (
              "Is already Student"
            ) : (
              <Button
                onClick={() => {
                  // handleAction({ type: "close" });
                  handleAction({ type: "confirm-close" });
                  handleAction({ type: "close" });
                  handleAction({ type: "mode-close" });
                  handleAction({ type: "modal-edit", data: row?.id });
                }}
              >
                Convert To Student
              </Button>
            )}
          </div>
        ),
      },
      {
        name: "Category",
        cell: (row) => (
          <div>
            <Badge
              className={cn(
                `${
                  row?.is_enterprise
                    ? "bg-green-50 text-green-500"
                    : row?.is_masterclass
                      ? "bg-yellow-50 text-yellow-500"
                      : "bg-blue-50 text-blue-500"
                }`,
                "capitalize",
              )}
            >
              {row?.category}
            </Badge>
          </div>
        ),
      },
      {
        name: "Phone",
        accessKey: "phone_no",
        cell: (row) => (
          <div className="text-sm text-gray-600">
            <a href={`tel:${row?.phone})`}>
              {" "}
              {isNullCheck(row?.phone) as string}
            </a>
          </div>
        ),
      },
      {
        name: "Festival",
        accessKey: "festival",
        cell: (row) => (
          <div className="text-sm text-gray-600">
            <Badge
              className={cn(
                `${
                  row?.is_enterprise
                    ? "bg-green-50 text-green-500"
                    : row?.is_masterclass
                      ? "bg-yellow-50 text-yellow-500"
                      : "bg-blue-50 text-blue-500"
                }`,
                "capitalize",
              )}
            >
              {row?.festival.name || "N/A"}
            </Badge>
          </div>
        ),
      },
      {
        name: "Profession",
        accessKey: "profession",
        cell: (row) => (
          <Tooltip description={row?.profession || "Null"}>
            <div className="text-sm text-gray-700 max-w-[140px] w-fit truncate">
              {isNullCheck(row?.profession) as string}
            </div>
          </Tooltip>
        ),
        sortable: true,
      },
    ],
    [handleAction],
  );

  return column;
};

export { ColumnList };
